import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { toast } from 'react-toastify'

const COPY_BLOCK_TOAST = 'Không được copy nội dung bài học'
const TOAST_ID = 'learning-copy-blocked'

type MenuState = { x: number; y: number; selectedText: string }

interface ContentCopyGuardProps {
  children: ReactNode
  className?: string
  /** When set, right-click shows Ask AI instead of only blocking the menu */
  onAskAi?: (selectedText: string) => void
  /** Disable Ask AI menu item (e.g. while taking a quiz) */
  askAiDisabled?: boolean
  askAiDisabledReason?: string
}

function notifyBlocked() {
  toast.info(COPY_BLOCK_TOAST, { toastId: TOAST_ID })
}

/**
 * Cho phép highlight text nhưng chặn copy/cut.
 * Chuột phải: mở menu Ask AI (nếu có onAskAi).
 */
export default function ContentCopyGuard({
  children,
  className,
  onAskAi,
  askAiDisabled = false,
  askAiDisabledReason = 'Hoàn thành / nộp bài rồi mới hỏi AI',
}: ContentCopyGuardProps) {
  const lastToastAt = useRef(0)
  const [menu, setMenu] = useState<MenuState | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const softNotify = useCallback(() => {
    const now = Date.now()
    if (now - lastToastAt.current < 1500) return
    lastToastAt.current = now
    notifyBlocked()
  }, [])

  const blockClipboard = useCallback(
    (e: ClipboardEvent) => {
      e.preventDefault()
      softNotify()
    },
    [softNotify]
  )

  const blockShortcut = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()
      if ((e.ctrlKey || e.metaKey) && (key === 'c' || key === 'x' || key === 'insert')) {
        e.preventDefault()
        softNotify()
      }
      if (e.shiftKey && key === 'delete') {
        e.preventDefault()
        softNotify()
      }
    },
    [softNotify]
  )

  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      if (!onAskAi) {
        softNotify()
        return
      }
      const selectedText = window.getSelection()?.toString()?.trim() || ''
      const pad = 8
      const menuW = 200
      const menuH = 48
      const x = Math.min(e.clientX, window.innerWidth - menuW - pad)
      const y = Math.min(e.clientY, window.innerHeight - menuH - pad)
      setMenu({ x: Math.max(pad, x), y: Math.max(pad, y), selectedText })
    },
    [onAskAi, softNotify]
  )

  useEffect(() => {
    if (!menu) return
    const close = (ev: Event) => {
      const target = ev.target as Node | null
      if (menuRef.current && target && menuRef.current.contains(target)) return
      setMenu(null)
    }
    const onKey = (ev: globalThis.KeyboardEvent) => {
      if (ev.key === 'Escape') setMenu(null)
    }
    window.addEventListener('mousedown', close)
    window.addEventListener('scroll', close, true)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', close)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [menu])

  return (
    <div
      className={className}
      tabIndex={0}
      onCopy={blockClipboard}
      onCut={blockClipboard}
      onContextMenu={handleContextMenu}
      onKeyDown={blockShortcut}
    >
      {children}

      {menu && onAskAi && (
        <div
          ref={menuRef}
          className="fixed z-[80] min-w-[180px] overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container py-1 shadow-xl"
          style={{ left: menu.x, top: menu.y }}
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            disabled={askAiDisabled}
            title={askAiDisabled ? askAiDisabledReason : 'Hỏi AI về nội dung này'}
            onClick={() => {
              if (askAiDisabled) {
                toast.info(askAiDisabledReason)
                setMenu(null)
                return
              }
              const text = menu.selectedText
              setMenu(null)
              onAskAi(text)
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-m2-primary hover:bg-m2-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="fas fa-robot text-xs" />
            Ask AI
            {menu.selectedText ? (
              <span className="ml-auto text-[10px] font-normal text-m2-on-surface-variant">
                đoạn chọn
              </span>
            ) : null}
          </button>
        </div>
      )}
    </div>
  )
}
